
import store from '@/store/index'
import Vue from 'vue'
import i18n from '@/plugins/i18n'
import { AddSimplifiedDemandsAndSuppliesInput, ExecuteMatchingInput, KeywordProcessContentInput, MatchRequest, RemoveSimplifiedDemandsAndSuppliesInput, SimplifiedDemand, SimplifiedSupply } from '@chainsight/networker-backend-axios-sdk';
import { translateMatchRequest } from '@/utils/translator'

export default Vue.extend({
	name: 'BusinessMatchContent',
	components: {
	},
	data() {
		return {
			overlay: false,
			isSearchLoading: false,
			progressValue: 0,
			progressStatusText: '',
			progressInterval: null as any,
			isMatching: false,
			snackbar: false,
			invalidQueryDialog: false,
			newQueryKeyword: '',
			snackbarText: '',
			snackbarColor: '',
			interval: 0,
			matchRequest: {} as MatchRequest,
			removeSimplifiedDemands: {} as SimplifiedDemand,
			removeSimplifiedSupplies: {} as SimplifiedSupply,
			simplifiedDemands: [] as SimplifiedDemand[],
			simplifiedSupplies: [] as SimplifiedSupply[],
			deleteDialog: false,
			addDialog: false,
			itemToDelete: null as SimplifiedDemand | SimplifiedSupply | null,
			deleteType: '' as 'demand' | 'supply' | '',
			hasAnyOptions: false,
			dataLoaded: false,
			newItemIds: [] as string[],
		};
	},
	computed: {
		trackDto(){
			return store.state.track.trackDto
		},
		user() {
			return store.state.user;
		},
		groupedAllItems() {
			const groups: { [key: string]: any } = {};
			
			// 合併 demands 和 supplies，並標記類型
			if (this.matchRequest?.simplifiedDemands) {
				this.matchRequest.simplifiedDemands.forEach(demand => {
					// 使用 categoryTitle 作為主要分群鍵值，如果沒有則使用 originalQuery
					const key = demand.categoryTitle || demand.originalQuery || '未分類';
					if (!groups[key]) {
						groups[key] = {
							items: [],
							categoryTitle: demand.categoryTitle || null,
							originalQuery: demand.originalQuery || null
						};
					}
					groups[key].items.push({ ...demand, type: 'demand' });
				});
			}
			
			if (this.matchRequest?.simplifiedSupplies) {
				this.matchRequest.simplifiedSupplies.forEach(supply => {
					// 使用 categoryTitle 作為主要分群鍵值，如果沒有則使用 originalQuery
					const key = supply.categoryTitle || supply.originalQuery || '未分類';
					if (!groups[key]) {
						groups[key] = {
							items: [],
							categoryTitle: supply.categoryTitle || null,
							originalQuery: supply.originalQuery || null
						};
					}
					groups[key].items.push({ ...supply, type: 'supply' });
				});
			}
			
			return groups;
		}
	},
	methods: {
		onSearchStarted() {
			this.isSearchLoading = true
			this.progressValue = 0
			this.progressStatusText = String(this.$t('common.progressStatus.analyzingRequest'))

			// 啟動進度條動畫 - 15秒分成4等份
			let startTime = Date.now()

			this.progressInterval = setInterval(() => {
				const elapsed = Date.now() - startTime
				const timer = elapsed / 1000 // 轉換為秒

				if (timer >= 15) {
					// 超過15秒，保持在99%
					this.progressValue = 99
					this.progressStatusText = String(this.$t('common.progressStatus.generatingResults'))
					return
				}

				if (timer <= 3.75) {
					// 第一等分：分析您的需求
					this.progressStatusText = String(this.$t('common.progressStatus.analyzingRequest'))
					this.progressValue = (timer / 3.75) * 24.75  // 0-24.75%
				} else if (timer > 3.75 && timer <= 7.5) {
					// 第二等分：搜尋相關成員
					this.progressStatusText = String(this.$t('common.progressStatus.searchingMembers'))
					this.progressValue = 24.75 + ((timer - 3.75) / 3.75) * 24.75  // 24.75-49.5%
				} else if (timer > 7.5 && timer <= 11.25) {
					// 第三等分：智慧配對中
					this.progressStatusText = String(this.$t('common.progressStatus.smartMatching'))
					this.progressValue = 49.5 + ((timer - 7.5) / 3.75) * 24.75  // 49.5-74.25%
				} else if (timer > 11.25 && timer <= 15) {
					// 第四等分：生成結果
					this.progressStatusText = String(this.$t('common.progressStatus.generatingResults'))
					this.progressValue = 74.25 + ((timer - 11.25) / 3.75) * 24.75  // 74.25-99%
				}
			}, 100)  // 每100ms更新一次，讓動畫更平滑
		},
		onSearchFinished() {
			// 完成動畫 - 與 UserProfile.vue 相同的邏輯
			this.progressValue = 100
			clearInterval(this.progressInterval)
			setTimeout(() => {
				this.isSearchLoading = false
				this.progressValue = 0
				this.progressStatusText = ''
			}, 500)
		},
		isAllSelected() {
			const totalDemands = this.matchRequest?.simplifiedDemands?.length || 0;
			const totalSupplies = this.matchRequest?.simplifiedSupplies?.length || 0;
			const selectedDemands = this.simplifiedDemands.length;
			const selectedSupplies = this.simplifiedSupplies.length;
			
			return totalDemands + totalSupplies > 0 && selectedDemands === totalDemands && selectedSupplies === totalSupplies;
		},
		async getMatchRequest(){
			try{
				let getMatchRequestResp
				getMatchRequestResp = await this.api$.businessMatchApi.getMatchRequest(this.user.userDto?.id as string, this.trackDto?.id? this.trackDto.id : '0')
				this.matchRequest = getMatchRequestResp.data.data

				if (i18n.locale === 'zh') {
					await translateMatchRequest(this.matchRequest, 'zh-Hant')
				} else if (i18n.locale === 'en') {
					await translateMatchRequest(this.matchRequest, 'en-US')
				} else if (i18n.locale === 'ja') {
					await translateMatchRequest(this.matchRequest, 'ja')
				}
				
				// 檢查是否有供需選項
				this.hasAnyOptions = ((this.matchRequest?.simplifiedDemands?.length ?? 0) > 0) || 
									((this.matchRequest?.simplifiedSupplies?.length ?? 0) > 0);
				
				// 預設全選所有項目
				if (this.matchRequest?.simplifiedDemands) {
					this.simplifiedDemands = this.matchRequest.simplifiedDemands.slice();
				}
				if (this.matchRequest?.simplifiedSupplies) {
					this.simplifiedSupplies = this.matchRequest.simplifiedSupplies.slice();
				}
				
				// 標記資料已載入
				this.dataLoaded = true;
			}catch (error) {
				console.error(error);
				this.dataLoaded = true; // 即使出錯也要標記已載入
			}
		},
		async executeMatching(){
			this.isMatching = true
			this.progressValue = 0

			const executeMatchingInput : ExecuteMatchingInput = {
				simplifiedDemands: this.simplifiedDemands,
				simplifiedSupplies: this.simplifiedSupplies
			}

			let executeMatchingResp
			try {
				executeMatchingResp = await this.api$.businessMatchApi.executeMatching(this.user.userDto?.id as string, this.trackDto?.id ? this.trackDto.id : '0', executeMatchingInput)

				this.progressValue = 100
				if(executeMatchingResp.data.isSuccess){
					setTimeout(() => {
						this.isMatching = false
						clearInterval(this.interval)
						this.$router.push({name:'BusinessMatchResults'})
					},	150)
				}
			} catch(error: any){
				setTimeout(() => {
					this.isMatching = false
					clearInterval(this.interval)
					this.snackbar = true
					const errorMessage = error?.response?.data?.error?.message || "";
					if (errorMessage.toLowerCase().includes("openai issue")) {
						this.snackbarText = String(this.$t('businessMatchContent.errors.openaiUnavailable'));
					} else {
						this.snackbarText = String(this.$t('businessMatchContent.errors.fetchMatchFailed'));
					}
					this.snackbarColor = 'error'
				},	100)
				console.log(error)
			}
		},
		async addSimplifiedDemandsAndSupplies(){
			this.overlay = true
			const addSimplifiedDemandAndSuppliesInput : AddSimplifiedDemandsAndSuppliesInput = {
				query : [this.newQueryKeyword]
			}
			let addSimplifiedDemandsAndSuppliesResp
			try{
				// 保存原始的 demands 和 supplies
				const originalDemands = JSON.stringify(this.matchRequest?.simplifiedDemands || [])
				const originalSupplies = JSON.stringify(this.matchRequest?.simplifiedSupplies || [])
				
				addSimplifiedDemandsAndSuppliesResp = await this.api$.businessMatchApi.addSimplifiedDemandsAndSupplies(this.user.userDto?.id as string, this.trackDto?.id ? this.trackDto.id : '0', addSimplifiedDemandAndSuppliesInput)
				const newMatchRequest = addSimplifiedDemandsAndSuppliesResp.data.data
				
				// 檢查新的 demands 和 supplies 是否與原始相同
				const newDemands = JSON.stringify(newMatchRequest?.simplifiedDemands || [])
				const newSupplies = JSON.stringify(newMatchRequest?.simplifiedSupplies || [])
				
				if (originalDemands === newDemands && originalSupplies === newSupplies) {
					// 如果相同，顯示 dialog
					this.invalidQueryDialog = true
					this.overlay = false
					return
				}
				
				this.matchRequest = newMatchRequest
				
				// 找出新增的選項並記錄（使用整個物件比較）
				const originalDemandsArray = JSON.parse(originalDemands);
				const originalSuppliesArray = JSON.parse(originalSupplies);
				
				// 建立原始項目的描述集合用於比較
				const originalDemandDescriptions = originalDemandsArray.map((d: any) => d.description || '');
				const originalSupplyDescriptions = originalSuppliesArray.map((s: any) => s.description || '');
				
				// 找出新增的項目描述
				const newDemandItems = (newMatchRequest?.simplifiedDemands || []).filter(d => !originalDemandDescriptions.includes(d.description));
				const newSupplyItems = (newMatchRequest?.simplifiedSupplies || []).filter(s => !originalSupplyDescriptions.includes(s.description));
				
				// 記錄新項目的描述
				const newDemandDescriptions = newDemandItems.map(d => d.description);
				const newSupplyDescriptions = newSupplyItems.map(s => s.description);
				this.newItemIds = newDemandDescriptions.concat(newSupplyDescriptions);
				
				// 將新的所有項目設為已勾選
				if (this.matchRequest?.simplifiedDemands) {
					this.simplifiedDemands = this.matchRequest.simplifiedDemands.slice();
				}
				if (this.matchRequest?.simplifiedSupplies) {
					this.simplifiedSupplies = this.matchRequest.simplifiedSupplies.slice();
				}
				
				if (i18n.locale === 'zh') {
					await translateMatchRequest(this.matchRequest, 'zh-Hant')
				} else if (i18n.locale === 'en') {
					await translateMatchRequest(this.matchRequest, 'en-US')
				} else if (i18n.locale === 'ja') {
					await translateMatchRequest(this.matchRequest, 'ja')
				}
				
				// 檢查是否有供需選項
				this.hasAnyOptions = ((this.matchRequest?.simplifiedDemands?.length ?? 0) > 0) || 
									((this.matchRequest?.simplifiedSupplies?.length ?? 0) > 0);
			} catch(error){
				console.log(error)
			}
			this.overlay = false
		},
		handleKeydownForAddSimplifiedDemandsAndSupplies(event) {
			if (this.newQueryKeyword.trim().length > 0 && event.key === 'Enter' ) {
				this.addDialog = false;
				this.addSimplifiedDemandsAndSupplies();
			}
		},
		handleAddItem() {
			if (!this.newQueryKeyword.trim()) return;
			
			this.addDialog = false;
			this.addSimplifiedDemandsAndSupplies();
		},
		closeAddDialog() {
			this.addDialog = false;
			this.newQueryKeyword = '';
		},
		async removeSimplifiedDemandsAndSupplies(){
			this.overlay = true
			const removeSimplifiedDemandsAndSuppliesInput : RemoveSimplifiedDemandsAndSuppliesInput = {
				removeSimplifiedDemands: [this.removeSimplifiedDemands],
				removeSimplifiedSupplies: [this.removeSimplifiedSupplies]
			}

			let removeSimplifiedDemandsAndSuppliesResp
			try{
				removeSimplifiedDemandsAndSuppliesResp = await this.api$.businessMatchApi.deleteSimplifiedDemandsAndSupplies(this.user.userDto?.id as string, this.trackDto?.id ? this.trackDto.id : '0', removeSimplifiedDemandsAndSuppliesInput)
				this.matchRequest = removeSimplifiedDemandsAndSuppliesResp.data.data
				
				if (i18n.locale === 'zh') {
					await translateMatchRequest(this.matchRequest, 'zh-Hant')
				} else if (i18n.locale === 'en') {
					await translateMatchRequest(this.matchRequest, 'en-US')
				} else if (i18n.locale === 'ja') {
					await translateMatchRequest(this.matchRequest, 'ja')
				}
				
				// 檢查是否有供需選項
				this.hasAnyOptions = ((this.matchRequest?.simplifiedDemands?.length ?? 0) > 0) || 
									((this.matchRequest?.simplifiedSupplies?.length ?? 0) > 0);
			} catch(error){
				console.log(error)
			}
			this.overlay = false
		},
		confirmDelete(item: SimplifiedDemand | SimplifiedSupply, type: 'demand' | 'supply') {
			this.itemToDelete = item;
			this.deleteType = type;
			this.deleteDialog = true;
		},
		async deleteItem() {
			if (!this.itemToDelete) return;
			
			// 移除 type 屬性後再設定
			const { type, ...itemWithoutType } = this.itemToDelete as any;
			
			if (type === 'demand') {
				this.removeSimplifiedDemands = itemWithoutType as SimplifiedDemand;
				this.removeSimplifiedSupplies = {} as SimplifiedSupply;
			} else {
				this.removeSimplifiedDemands = {} as SimplifiedDemand;
				this.removeSimplifiedSupplies = itemWithoutType as SimplifiedSupply;
			}
			
			this.deleteDialog = false;
			await this.removeSimplifiedDemandsAndSupplies();
			this.itemToDelete = null;
			this.deleteType = '';
		},
		isItemSelected(item: any) {
			if (item.type === 'demand') {
				return this.simplifiedDemands.some((d: SimplifiedDemand) => 
					d.description === item.description && d.originalQuery === item.originalQuery && d.companyQuery === item.companyQuery && d.reason === item.reason
				);
			} else {
				return this.simplifiedSupplies.some((s: SimplifiedSupply) => 
					s.description === item.description && s.originalQuery === item.originalQuery && s.companyQuery === item.companyQuery && s.reason === item.reason
				);
			}
		},
		toggleItem(item: any) {
			const { type, ...itemWithoutType } = item;
			
			if (type === 'demand') {
				const index = this.simplifiedDemands.findIndex((d: SimplifiedDemand) => 
					d.description === item.description && d.originalQuery === item.originalQuery && d.companyQuery === item.companyQuery && d.reason === item.reason
				);
				
				if (index > -1) {
					this.simplifiedDemands.splice(index, 1);
				} else {
					this.simplifiedDemands.push(itemWithoutType as SimplifiedDemand);
				}
			} else {
				const index = this.simplifiedSupplies.findIndex((s: SimplifiedSupply) => 
					s.description === item.description && s.originalQuery === item.originalQuery && s.companyQuery === item.companyQuery && s.reason === item.reason
				);
				
				if (index > -1) {
					this.simplifiedSupplies.splice(index, 1);
				} else {
					this.simplifiedSupplies.push(itemWithoutType as SimplifiedSupply);
				}
			}
		},
		toggleSelectAll() {
			if (this.isAllSelected()) {
				// 如果已全選，則取消所有選擇
				this.simplifiedDemands = [];
				this.simplifiedSupplies = [];
			} else {
				// 如果未全選，則選擇所有項目
				this.simplifiedDemands = [];
				this.simplifiedSupplies = [];
				
				// 選擇所有需求項目
				if (this.matchRequest?.simplifiedDemands) {
					this.simplifiedDemands = this.matchRequest.simplifiedDemands.slice();
				}
				
				// 選擇所有供給項目
				if (this.matchRequest?.simplifiedSupplies) {
					this.simplifiedSupplies = this.matchRequest.simplifiedSupplies.slice();
				}
			}
		}
	},
	watch: {
		addDialog(newVal) {
			if (!newVal) {
				this.newQueryKeyword = '';
			}
		},
		isMatching: {
			deep: true,
			handler: function () {
				this.progressValue = 0
				let timer = 0
				this.interval = setInterval(() => {
					if (this.progressValue >= 98) {
						clearInterval(this.interval)
						return;
					}
					
					timer += 0.1;

					if (timer <= 30) {
						this.progressValue += 4 / 15
					} else if (timer > 30 && timer <= 60) {
						this.progressValue += 1 / 60
					} else if (timer > 60 && timer <= 120) {
						this.progressValue += 1 / 75
					}

					if (this.progressValue > 98) {
						this.progressValue = 98;
						clearInterval(this.interval)
					}
				}, 100)
			}
		},
		'$i18n.locale': {
			immediate: false,
			async handler() {
				this.overlay = true
				if (i18n.locale === 'zh') {
					await translateMatchRequest(this.matchRequest, 'zh-Hant')
				} else if (i18n.locale === 'en') {
					await translateMatchRequest(this.matchRequest, 'en-US')
				} else if (i18n.locale === 'ja') {
					await translateMatchRequest(this.matchRequest, 'ja')
				}
				this.overlay = false
			}
		}
	},
	async created() {
		this.overlay = true
		await this.getMatchRequest()
		this.overlay = false
	},
	mounted() {
		// 監聽搜尋事件
		this.eventHub$.$on('searchStarted', this.onSearchStarted)
		this.eventHub$.$on('searchFinished', this.onSearchFinished)
	},
	beforeDestroy() {
		// 移除 eventHub 監聽器
		this.eventHub$.$off('searchStarted', this.onSearchStarted)
		this.eventHub$.$off('searchFinished', this.onSearchFinished)
	}
});
